import getLang from 'I18n/utils/getLang';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { CheckCircleIcon } from 'foundations-assets/icons/components/CheckCircleIcon';
import { ThumbsDownIcon } from 'foundations-assets/icons/components/ThumbsDownIcon';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Small } from 'foundations-components/transitional/components/Small';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import { MediaQueryView } from 'marketplace-ui-common/layout/components/MediaQueryView';
import Rating from 'marketplace-ui-reviews/components/Rating';
import { WriteReviewButton } from 'marketplace-ui-reviews/components/WriteReviewButton';
import { EcosystemReviewsList } from 'marketplace-ui-reviews/components/display/EcosystemReviewsList';
import { EcosystemReviewsStats } from 'marketplace-ui-reviews/components/display/EcosystemReviewsStats';
import { AppReviewModal } from 'marketplace-ui-reviews/components/modals/AppReviewModal';
import { SolutionsPartnerProfileReviewModal } from 'marketplace-ui-reviews/components/modals/SolutionsPartnerProfileReviewModal';
import { MAX_USER_REVIEWS_FOR_ENTITY } from 'marketplace-ui-reviews/constants/UnreviewableReason';
import { useReviewFlowContext } from 'marketplace-ui-reviews/contexts/ReviewFlowContext';
import { ReviewThemeTags } from 'marketplace-ui-reviews/components/display/ReviewThemeTags';
import { useAIReviewSummary } from 'marketplace-ui-reviews/hooks/useAIReviewSummary';
import { useReviewThemes } from 'marketplace-ui-reviews/hooks/useReviewThemes';
import { useReviews } from 'marketplace-ui-reviews/hooks/useReviews';
import { useSearchReviewsLegacy } from 'marketplace-ui-reviews/hooks/useSearchReviewsLegacy';
import ReviewFilters from 'marketplace-ui-reviews/records/ReviewFilters';
import { gate } from 'hub-http/gates';
import { getReviewTypeFromProductType } from 'marketplace-ui-reviews/utils/getReviewTypeFromProductType';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import ReviewRenderer from '../../reviews/ReviewRenderer';
import { getAppDetailsShortlink } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { DetailsPageCard } from './DetailsPageCard';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const REVIEW_CARD_SECTION = 'product reviews card';
const AI_SUMMARY_SUBSCREEN = 'ai-review-summary';
export function ProductReviewsCard({
  companyName,
  isPreview,
  isReviewLink,
  productId,
  slug,
  userInfo
}) {
  var _userInfo$user, _userInfo$gates;
  const userId = userInfo === null || userInfo === void 0 || (_userInfo$user = userInfo.user) === null || _userInfo$user === void 0 ? void 0 : _userInfo$user.user_id;
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const {
    appContext,
    iconUrl,
    name,
    productType,
    trackInteraction,
    listingId
  } = useDetailsPageContext();
  const {
    editedReview,
    editedReviewFetchStatus,
    hasEditableReview,
    onReviewSubmitted,
    reviewabilityRequestStatus,
    unreviewableReason
  } = useReviewFlowContext();
  useEffect(() => {
    if (appContext === 'app' && isReviewLink) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setReviewModalOpen(true);
    }
  }, [isReviewLink, appContext]);
  const isReviewDataReady = !!userId && (editedReviewFetchStatus === 'SUCCEEDED' || editedReviewFetchStatus === 'FAILED') && reviewabilityRequestStatus === 'SUCCEEDED' && (unreviewableReason !== `ReviewError.${MAX_USER_REVIEWS_FOR_ENTITY}` || !!editedReview);
  const reviewType = getReviewTypeFromProductType(productType);
  const useChirpSearch = !!(userInfo !== null && userInfo !== void 0 && (_userInfo$gates = userInfo.gates) !== null && _userInfo$gates !== void 0 && _userInfo$gates.includes(gate('Reviews:SearchLegacyChirp')));
  const [chirpFilters, setChirpFilters] = useState(new ReviewFilters({
    entityId: productId,
    limit: 2
  }));
  const chirpData = useSearchReviewsLegacy({
    appContext,
    // APP/PROFILE reviews are indexed by framework listing ID, not offering/profile ID.
    entityId: listingId,
    reviewFilters: chirpFilters,
    reviewType,
    skip: !useChirpSearch
  });
  const {
    reviewSummary,
    reviewSummaryLastUpdated
  } = useAIReviewSummary({
    appContext,
    entityId: productId,
    language: getLang(),
    reviewType
  });
  const {
    themes
  } = useReviewThemes({
    entityId: productId,
    language: getLang(),
    reviewType,
    skip: !reviewSummary
  });
  const [excerptSearchTerm, setExcerptSearchTerm] = useState(null);

  //begin AI Review summary block
  const showAIReviewSummary = useMemo(() => !!reviewSummary, [reviewSummary]);
  const [reviewSummaryRating, setReviewSummaryRating] = useState(0);
  const trackedAIReviewSummaryRender = useRef(false);
  const trackedAIReviewSummaryHovered = useRef(false);
  const trackedAIReviewSummaryClicked = useRef(false);
  const trackAIReviewSummaryAction = useCallback(action => {
    trackInteraction({
      action,
      value: `entityId: ${productId}`,
      section: REVIEW_CARD_SECTION,
      subscreen: AI_SUMMARY_SUBSCREEN
    });
  }, [productId, trackInteraction]);
  function handleReviewSummaryRatingClicked(rating) {
    setReviewSummaryRating(rating);
    trackAIReviewSummaryAction(`AI review summary feedback: ${rating > 0 ? 'positive' : 'negative'}`);
  }
  useEffect(() => {
    if (showAIReviewSummary === true && !trackedAIReviewSummaryRender.current) {
      trackAIReviewSummaryAction('AI review summary rendered');
      trackedAIReviewSummaryRender.current = true;
    }
  }, [listingId, name, showAIReviewSummary, trackAIReviewSummaryAction]);
  //end AI review summary block

  const {
    reviewStats,
    reviewsCount
  } = useReviews({
    appContext,
    reviewFilters: new ReviewFilters({
      entityId: productId,
      reviewTypes: [reviewType]
    }),
    offeringId: productId,
    reviewType
  });
  const hasReviews = reviewsCount > 0;
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: REVIEW_CARD_SECTION,
      value: `entityId: ${productId}`
    }, eventProps));
  }, [productId, trackInteraction]);
  const handleWriteReviewButtonClick = useCallback(() => {
    if (appContext === 'public') {
      return;
    }
    setReviewModalOpen(true);
  }, [appContext]);
  let renderedReviewWizard = null;
  switch (productType) {
    case 'APP':
      renderedReviewWizard = /*#__PURE__*/_jsx(AppReviewModal, {
        appIconUrl: iconUrl,
        appName: name,
        editedReview: editedReview,
        isPublic: appContext === 'public',
        offeringId: productId,
        onClose: () => {
          setReviewModalOpen(false);
        },
        onSuccess: () => {
          onReviewSubmitted();
          setReviewModalOpen(false);
        },
        onTrackInteraction: handleTrackInteraction,
        unreviewableReason: unreviewableReason
      });
      break;
    case 'SOLUTIONS_PARTNER_PROFILE':
      renderedReviewWizard = /*#__PURE__*/_jsx(SolutionsPartnerProfileReviewModal, {
        editedReview: editedReview,
        iconUrl: iconUrl,
        isPublic: appContext === 'public',
        name: name || companyName || '',
        onClose: () => {
          setReviewModalOpen(false);
        },
        onSuccess: () => {
          onReviewSubmitted();
          setReviewModalOpen(false);
        },
        onTrackInteraction: handleTrackInteraction,
        profileId: productId,
        unreviewableReason: unreviewableReason
      });
      break;
    default:
      break;
  }
  const renderedWriteReviewButton = /*#__PURE__*/_jsx(LoadingWrapper, {
    requestStatus: reviewabilityRequestStatus,
    children: /*#__PURE__*/_jsx(WriteReviewButton, {
      disabled: isPreview,
      hasEditableReview: hasEditableReview,
      href: appContext === 'public' && slug ? `${getAppDetailsShortlink(slug)}/write-review?referrer=public-marketplace` : undefined,
      onClick: handleWriteReviewButtonClick,
      reviewPending: false,
      size: "sm",
      responsive: false,
      unreviewableReason: unreviewableReason
    })
  });
  const renderedEmptyState = /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h7",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.reviewsCard.empty"
      })
    }), /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      gap: "200",
      marginBlockEnd: "200",
      children: [/*#__PURE__*/_jsx(Rating, {
        direction: "column",
        rating: 0,
        type: "STAR_RATING_EMPTY_STATE"
      }), /*#__PURE__*/_jsx(Text, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.reviewsCard.emptyReviews"
        })
      })]
    }), renderedWriteReviewButton]
  });
  return /*#__PURE__*/_jsxs(DetailsPageCard, {
    id: "details-page-reviews",
    name: "reviews card",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.reviewsCard.title"
    }),
    actions: hasReviews ? renderedWriteReviewButton : null,
    children: [/*#__PURE__*/_jsxs(Grid, {
      gap: "600",
      children: [!!reviewStats.count && reviewStats.count > 3 && /*#__PURE__*/_jsxs(GridItem, {
        size: 12,
        children: [/*#__PURE__*/_jsx(MediaQueryView, {
          max: "lg",
          children: /*#__PURE__*/_jsx(EcosystemReviewsStats, {
            appContext: appContext,
            flexDirection: "column",
            justifyContent: "space-between",
            offeringId: productId,
            ratingKey: "OVERALL",
            reviewType: reviewType
          })
        }), /*#__PURE__*/_jsx(MediaQueryView, {
          min: "lg",
          children: /*#__PURE__*/_jsx(EcosystemReviewsStats, {
            appContext: appContext,
            justifyContent: "space-between",
            offeringId: productId,
            ratingKey: "OVERALL",
            reviewType: reviewType
          })
        })]
      }), /*#__PURE__*/_jsxs(GridItem, {
        size: 12,
        children: [showAIReviewSummary && /*#__PURE__*/_jsxs(View, {
          backgroundColor: "fill-surface-recessed",
          borderRadius: "200",
          marginBlockEnd: "600",
          padding: "800",
          onMouseEnter: () => {
            if (!trackedAIReviewSummaryHovered.current) {
              trackedAIReviewSummaryHovered.current = true;
              trackAIReviewSummaryAction('AI review summary hover');
            }
          },
          children: [/*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            inlineSize: "100%",
            justifyContent: "space-between",
            marginBlockEnd: "400",
            onClick: () => {
              if (!trackedAIReviewSummaryClicked.current) {
                trackedAIReviewSummaryClicked.current = true;
                trackAIReviewSummaryAction('AI review summary clicked');
              }
            },
            children: [/*#__PURE__*/_jsx(Text, {
              as: "small",
              color: "text-core-subtle",
              typography: "heading-25",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.reviewsCard.summarizedWithAI"
              })
            }), /*#__PURE__*/_jsx(AIBadge, {})]
          }), /*#__PURE__*/_jsx(Text, {
            children: reviewSummary
          }), /*#__PURE__*/_jsxs(Flex, {
            alignItems: "end",
            inlineSize: "100%",
            gap: "200",
            children: [reviewSummaryLastUpdated && /*#__PURE__*/_jsx(View, {
              marginInlineEnd: "auto",
              children: /*#__PURE__*/_jsx(Small, {
                children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                  message: "detailsPage.reviewsCard.summaryLastUpdated_jsx",
                  elements: {
                    Date: FormattedDateTime
                  },
                  options: {
                    value: reviewSummaryLastUpdated,
                    format: 'll',
                    type: 'userTz'
                  }
                })
              })
            }), reviewSummaryRating !== 0 ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(CheckCircleIcon, {
                color: "fill-accent-green-default"
              }), /*#__PURE__*/_jsx(Text, {
                color: "text-core-subtle",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.reviewsCard.thankYouForYourFeedback"
                })
              })]
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(IconButton, {
                use: "tertiary-transparent",
                shape: "circle",
                tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.reviewsCard.helpful"
                }),
                size: "small",
                onClick: () => handleReviewSummaryRatingClicked(1),
                children: /*#__PURE__*/_jsx(ThumbsUpIcon, {})
              }), /*#__PURE__*/_jsx(IconButton, {
                use: "tertiary-transparent",
                shape: "circle",
                tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.reviewsCard.unhelpful"
                }),
                size: "small",
                onClick: () => handleReviewSummaryRatingClicked(-1),
                children: /*#__PURE__*/_jsx(ThumbsDownIcon, {})
              })]
            })]
          }), /*#__PURE__*/_jsx(Text, {
            as: "small",
            color: "text-core-subtle",
            marginBlockStart: "200",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.reviewsCard.aiSummaryDisclaimer"
            })
          }), themes && themes.length > 0 && /*#__PURE__*/_jsx(ReviewThemeTags, {
            themes: themes,
            onThemeSelected: () => trackAIReviewSummaryAction('AI review theme tag selected'),
            onExcerptReadMore: excerptText => {
              setExcerptSearchTerm(excerptText);
              trackAIReviewSummaryAction('AI review theme excerpt read more clicked');
            }
          })]
        }), /*#__PURE__*/_jsx(EcosystemReviewsList, {
          appContext: appContext,
          collapsedPageLimit: 2,
          expandable: true,
          expandButtonText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.reviewsCard.seeAll"
          }),
          externalSearchTerm: excerptSearchTerm,
          offeringId: productId,
          onFiltersChange: useChirpSearch ? setChirpFilters : undefined,
          overrideData: useChirpSearch ? chirpData : undefined,
          pageLimitOptions: [5, 10, 20],
          renderedEmptyState: renderedEmptyState,
          ReviewRenderer: ReviewRenderer,
          reviewRendererProps: {
            reviewType,
            onTrackInteraction: handleTrackInteraction,
            userId
          },
          reviewType: reviewType,
          searchable: true,
          showPagination: true
        })]
      })]
    }), reviewModalOpen && isReviewDataReady && renderedReviewWizard]
  });
}